import { IBalusterGeometry, IRailingGeometry, IStepGeometry, IStringerGeometry, IVec3 } from '@stair-craft/common/util';

import { STRINGER_INSET, SUPPORT_POST_GROOVE } from '../../constants';
import { kosourRunAxis } from '../shared-board-profile/board-profile';
import { cutAtPost } from '../shared-turn-support-posts/turn-support-posts';

/** Heights within this much count as one level (mm). */
const FLAT: number = 1;

/** Squares closer than this in plan do not count as overlapping (mm). */
const SLACK: number = 0.5;

/** The side of the stair a board lies on, as the 3D builder places it: left boards grow inward from 0. */
function sideSign(side: IStringerGeometry['side']): number {
    if (side === 'left') {
        return 1;
    }
    return side === 'right' ? -1 : 0;
}

/** Whether a board is a sloped kosour one: not a turn block, not a тетива, not the spiral pole. */
function isSlopedKosour(board: IStringerGeometry): boolean {
    return !board.block && !board.bottom && board.side !== 'center' && board.points.length >= 2;
}

/** A sloped kosour board in plan: its centre across the run and its run range. */
function kosourPlan(board: IStringerGeometry): {
    across: number;
    start: number;
    end: number;
    runOf: (p: IVec3) => number;
    runAlongX: boolean;
} {
    const { runAlongX, runOf, fixedCoord } = kosourRunAxis(board.points);
    const runs: number[] = board.points.map(runOf);
    return {
        runAlongX,
        runOf,
        across: fixedCoord + sideSign(board.side) * (board.thickness / 2 + STRINGER_INSET),
        start: Math.min(...runs),
        end: Math.max(...runs),
    };
}

/**
 * Cuts a sloped kosour board square at the face of a post from the floor it runs into, leaving
 * the groove's depth inside the post: the end nearer the post is the one cut.
 */
function cutKosourAtPost(board: IStringerGeometry, post: IBalusterGeometry, half: number): void {
    if (!isSlopedKosour(board)) {
        return;
    }
    const { runAlongX, runOf, across, start, end } = kosourPlan(board);
    const postAcross: number = runAlongX ? post.position.z : post.position.x;
    const postRun: number = runOf(post.position);
    const apart: boolean =
        Math.abs(postAcross - across) >= half + board.thickness / 2 - SLACK ||
        postRun - half >= end - SLACK ||
        postRun + half <= start + SLACK;
    if (apart) {
        return;
    }
    const span: NonNullable<IStringerGeometry['runSpan']> = { ...board.runSpan };
    if (postRun < (start + end) / 2) {
        span.from = Math.max(span.from ?? start, postRun + half - SUPPORT_POST_GROOVE);
    } else {
        span.to = Math.min(span.to ?? end, postRun - half + SUPPORT_POST_GROOVE);
    }
    board.runSpan = span;
}

/** The part of a polyline on one side of a run coordinate, with the crossing point added. */
function clipAtRun(points: readonly IVec3[], runOf: (p: IVec3) => number, cut: number, keepAbove: boolean): IVec3[] {
    const keeps: (p: IVec3) => boolean = (p: IVec3): boolean => (keepAbove ? runOf(p) >= cut : runOf(p) <= cut);
    const result: IVec3[] = [];
    points.forEach((point: IVec3, i: number) => {
        if (keeps(point)) {
            result.push(point);
        }
        const next: IVec3 = points[Math.min(i + 1, points.length - 1)];
        if (keeps(point) !== keeps(next)) {
            const t: number = (cut - runOf(point)) / (runOf(next) - runOf(point));
            result.push({ x: point.x + t * (next.x - point.x), y: point.y + t * (next.y - point.y), z: point.z + t * (next.z - point.z) });
        }
    });
    return result;
}

/** The lowest height a polyline has over a run coordinate, or null where it does not reach. */
function lowestAtRun(points: readonly IVec3[], runOf: (p: IVec3) => number, run: number): number | null {
    let lowest: number | null = null;
    for (let i: number = 0; i < points.length - 1; i++) {
        const from: number = runOf(points[i]);
        const to: number = runOf(points[i + 1]);
        if (run >= Math.min(from, to) && run <= Math.max(from, to) && from !== to) {
            const y: number = points[i].y + ((run - from) / (to - from)) * (points[i + 1].y - points[i].y);
            lowest = Math.min(lowest ?? Infinity, y);
        }
    }
    return lowest;
}

/** Whether a post's square meets a тетива in plan and reaches up to it. */
function postMeetsTetiva(board: IStringerGeometry, bottom: readonly IVec3[], post: IBalusterGeometry, half: number): boolean {
    const { runAlongX, runOf, fixedCoord } = kosourRunAxis(board.points);
    const runs: number[] = board.points.map(runOf);
    const postRun: number = runOf(post.position);
    const across: number = Math.abs((runAlongX ? post.position.z : post.position.x) - fixedCoord);
    const under: boolean = post.position.y + post.height <= (lowestAtRun(bottom, runOf, postRun) ?? -Infinity);
    const apart: boolean = postRun - half >= Math.max(...runs) - SLACK || postRun + half <= Math.min(...runs) + SLACK;
    return !under && !apart && across < half + board.thickness - SLACK;
}

/**
 * Cuts a тетива square at the face of a railing post standing on its axis, leaving the groove's
 * depth inside the post (SC-CALC-094): the end nearer the post is the one cut, top and bottom edge
 * alike. A post whose top stays under the board — a U stair without a gap stands the lower flight's
 * bottom post under the upper flight's board — leaves it whole.
 */
function cutTetivaAtPost(board: IStringerGeometry, post: IBalusterGeometry, half: number): void {
    const bottom: IVec3[] | undefined = board.block ? undefined : board.bottom;
    if (!bottom || board.side === 'center' || board.points.length < 2) {
        return;
    }
    const { runOf } = kosourRunAxis(board.points);
    const runs: number[] = board.points.map(runOf);
    const postRun: number = runOf(post.position);
    if (postMeetsTetiva(board, bottom, post, half)) {
        const nearStart: boolean = postRun < (Math.min(...runs) + Math.max(...runs)) / 2;
        const cut: number = postRun + (nearStart ? 1 : -1) * (half - SUPPORT_POST_GROOVE);
        board.points = clipAtRun(board.points, runOf, cut, nearStart);
        board.bottom = clipAtRun(bottom, runOf, cut, nearStart);
    }
}

/**
 * Brings every post of the stair's own railing that stands on a tread or a landing down to the
 * floor (SC-CALC-094): the post keeps its top, the treads it passes through are notched around it
 * and a kosour board ends in its groove. The exit post — on the last tread or the slab — stays
 * where it is: down to the floor it would run through the whole flight under it. A тетива ends in
 * the groove of every post standing on its axis, the exit post included. The upper-floor railing
 * is not touched.
 */
export function bringPostsToFloor(
    railings: readonly IRailingGeometry[],
    steps: readonly IStepGeometry[],
    stringers: readonly IStringerGeometry[]
): void {
    const lastTop: number = Math.max(0, ...steps.map((step: IStepGeometry) => step.position.y));
    for (const railing of railings.filter((item: IRailingGeometry) => item.kind !== 'upper')) {
        for (const post of railing.posts.filter((item: IBalusterGeometry) => item.position.y < lastTop - FLAT)) {
            post.height += post.position.y;
            post.position.y = 0;
            for (const board of stringers) {
                cutKosourAtPost(board, post, railing.postWidth / 2);
                cutAtPost(board, post.position, railing.postWidth / 2);
            }
        }
        for (const post of railing.posts) {
            for (const board of stringers) {
                cutTetivaAtPost(board, post, railing.postWidth / 2);
            }
        }
    }
}
